import {
  FlexibleDatesResult,
  FlexibleDatesMappedResult,
  FlexibleDatesCategoryResult,
  FlexibleDatesSectorResult
} from '@flight-results/entities/flexible.dates';
import { ByPriceOption, ByDate } from '@flight-results/components/mobile-flexible-dates/entities/mobile-flexible-dates.entities';
import { getDaysBetweenDates, getUTCDate } from '@flight-results/entities/date-validation';
import { FlexibleDatesPayload } from '@flight-results/entities/flexible-dates.payload';
import { formatDateByRegex } from '@ux/mappers/date.mapper';

export function flexDatesMapper(results: FlexibleDatesResult, payload: FlexibleDatesPayload): FlexibleDatesMappedResult {
  const departureD = formatDateByRegex(payload.Trips[0].DepartureDate);
  const arrivalD = formatDateByRegex(payload.Trips[payload.Trips.length - 1].DepartureDate);
  return {
    ByPrice: mapByPrice(results.ByPrice, results.Results, departureD, arrivalD).sort(sortByCurrent),
    ByLengthOfStay: mapByLengthOfStay(results.ByLengthOfStay, results.Results, departureD, arrivalD),
    ByFixingDepartureAndChangingReturnTrip: mapByFixingDepartureAndChangingReturnTrip(
      results.OriginalDeparture, results.Results, arrivalD
    ),
    ByFixingReturnAndChangingDepartureDate: mapByFixingReturnAndChangingDepartureDate(
      results.OriginalReturn, results.Results, departureD
    ),
    ByPriceOneWay: mapByPriceOneWay(results.ByPrice, results.Results, departureD).sort(sortByCurrent),
    Results: results.Results
  } as FlexibleDatesMappedResult;
}

function mapByPrice(byPrice: FlexibleDatesCategoryResult[],
  results: FlexibleDatesSectorResult[],
  departureDate: Date,
  arrivalDate: Date): ByPriceOption[] {
  const elementsToDisplay = 7;
  return byPrice?.slice(0, elementsToDisplay).map(p => {
    const resultOption = results.find(r => r.OptionId === p.OptionId);
    return <ByPriceOption>{
      OptionId: p.OptionId,
      PriceDifference: p.PriceDifference,
      DepartureDate: resultOption?.Sectors[0].Flights[0].DepartureDate,
      ArrivalDate: resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate,
      TotalPrice: resultOption?.Price.Total,
      CurrencyCode: resultOption?.Price.CurrencyCode,
      IsCurrentDate: getDaysBetweenDates(arrivalDate,
        getUTCDate(resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate)) === 0
        && getDaysBetweenDates(departureDate,
          getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)) === 0
    };
  });
}

function mapByLengthOfStay(byLengthOfStay: FlexibleDatesCategoryResult[],
  results: FlexibleDatesSectorResult[],
  departureDate: Date,
  arrivalDate: Date): ByPriceOption[] {
  return byLengthOfStay?.map(p => {
    const resultOption = results.find(r => r.OptionId === p.OptionId);
    return <ByPriceOption>{
      OptionId: p.OptionId,
      PriceDifference: p.PriceDifference,
      DepartureDate: resultOption?.Sectors[0].Flights[0].DepartureDate,
      ArrivalDate: resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate,
      TotalPrice: resultOption?.Price.Total,
      CurrencyCode: resultOption?.Price.CurrencyCode,
      DaysDifference: getDaysBetweenDates(departureDate,
        getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)),
      IsCurrentDate: getDaysBetweenDates(arrivalDate,
        getUTCDate(resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate)) === 0
        && getDaysBetweenDates(departureDate,
          getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)) === 0
    };
  }).sort((a, b) => a.DaysDifference - b.DaysDifference);
}

function mapByFixingDepartureAndChangingReturnTrip(byOriginalDeparture: FlexibleDatesCategoryResult[],
  results: FlexibleDatesSectorResult[],
  arrivalDate: Date): ByDate[] {
  return byOriginalDeparture?.map(o => {
    const resultOption = results.find(r => r.OptionId === o.OptionId);
    return <ByDate>{
      OptionId: o.OptionId,
      PriceDifference: o.PriceDifference,
      CurrencyCode: resultOption?.Price.CurrencyCode,
      TotalPrice: resultOption?.Price.Total,
      DaysDifference: getDaysBetweenDates(arrivalDate,
        getUTCDate(resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate)),
      IsCurrentDate: getDaysBetweenDates(arrivalDate,
        getUTCDate(resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate)) === 0,
      DepartureDate: resultOption?.Sectors[0].Flights[0].DepartureDate,
      ArrivalDate: resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate,
    };
  }).sort((a, b) => a.DaysDifference - b.DaysDifference);
}

function mapByFixingReturnAndChangingDepartureDate(byOriginalReturn: FlexibleDatesCategoryResult[],
  results: FlexibleDatesSectorResult[],
  departureDate: Date
): ByDate[] {
  return byOriginalReturn?.map(o => {
    const resultOption = results.find(r => r.OptionId === o.OptionId);
    return <ByDate>{
      OptionId: o.OptionId,
      PriceDifference: o.PriceDifference,
      CurrencyCode: resultOption?.Price.CurrencyCode,
      TotalPrice: resultOption?.Price.Total,
      DaysDifference: getDaysBetweenDates(departureDate,
        getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)),
      IsCurrentDate: getDaysBetweenDates(departureDate,
        getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)) === 0,
      DepartureDate: resultOption?.Sectors[0].Flights[0].DepartureDate,
      ArrivalDate: resultOption?.Sectors[resultOption.Sectors.length - 1].Flights[0].DepartureDate,
    };
  }).sort((a, b) => a.DaysDifference - b.DaysDifference);
}

function mapByPriceOneWay(byPrice: FlexibleDatesCategoryResult[],
  results: FlexibleDatesSectorResult[],
  departureDate: Date): ByDate[] {
  return byPrice?.map(o => {
    const resultOption = results.find(r => r.OptionId === o.OptionId);
    return <ByDate>{
      OptionId: o.OptionId,
      PriceDifference: o.PriceDifference,
      CurrencyCode: resultOption?.Price.CurrencyCode,
      TotalPrice: resultOption?.Price.Total,
      DaysDifference: getDaysBetweenDates(departureDate,
        getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)),
      IsCurrentDate: getDaysBetweenDates(departureDate,
        getUTCDate(resultOption?.Sectors[0].Flights[0].DepartureDate)) === 0,
      DepartureDate: resultOption?.Sectors[0].Flights[0].DepartureDate
    };
  }).sort((a, b) => a.DaysDifference - b.DaysDifference);
}

function sortByCurrent(a: ByDate | ByPriceOption, b: ByDate | ByPriceOption): number {
  return Number(!!(b.IsCurrentDate)) - Number(!!(a.IsCurrentDate));
}
